// Settings → Security: where you're signed in, and the two ways out.
// See src/lib/sessions.ts for what each button does and its limit. The
// sidebar's Sign Out ends only this device; Sign out everywhere ends them all.
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2, Monitor } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useAuth } from '@/hooks/useAuth';
import { useImpersonation } from '@/contexts/ImpersonationContext';
import { describeUserAgent } from '@/lib/errorIdentity';
import { sanitizeError } from '@/lib/errorMessages';
import { timeAgo } from '@/components/org/hierarchyLive';
import { loadMySessions, signOutOtherDevices } from '@/lib/sessions';

export default function SecuritySettings() {
  const { user, signOutEverywhere } = useAuth();
  const { isImpersonating } = useImpersonation();
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState<'others' | 'all' | null>(null);
  const [confirmAll, setConfirmAll] = useState(false);

  const key = ['my-sessions', user?.id];
  const { data: sessions = [], isLoading, isError } = useQuery({
    queryKey: key,
    queryFn: loadMySessions,
    // Under View-as the database would answer with the admin's own logins.
    enabled: !!user && !isImpersonating,
  });
  const others = sessions.filter(s => !s.is_current);

  const endOthers = async () => {
    setBusy('others');
    try {
      await signOutOtherDevices();
      await queryClient.invalidateQueries({ queryKey: key });
      toast.success('Signed out of your other devices');
    } catch (e) {
      toast.error('Could not sign out your other devices', { description: sanitizeError(e) });
    } finally {
      setBusy(null);
    }
  };

  const endAll = async () => {
    setBusy('all');
    try {
      await signOutEverywhere();
    } catch (e) {
      toast.error('Could not sign out', { description: sanitizeError(e) });
      setBusy(null);
    }
  };

  if (isImpersonating) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Where you're signed in</CardTitle>
          <CardDescription>Not shown while viewing as someone else.</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Where you're signed in</CardTitle>
        <CardDescription>
          Every device and browser signed in to your account. Signing out ends a device's
          login; one that is open right now stops at its next check, within the hour.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? (
          <p className="text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading</p>
        ) : isError ? (
          <p className="text-sm text-muted-foreground">Your sign-ins could not be loaded right now.</p>
        ) : (
          <ul className="divide-y rounded-lg border" data-testid="my-sessions">
            {sessions.map(s => (
              <li key={s.id} className="flex items-start gap-3 p-3" data-testid="my-session">
                <Monitor className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium flex items-center gap-2">
                    {describeUserAgent(s.user_agent)}
                    {s.is_current && <Badge variant="secondary" className="text-[10px]">This device</Badge>}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Signed in {new Date(s.created_at).toLocaleDateString()} · Active {timeAgo(s.last_active_at)}
                    {s.ip ? ` · ${s.ip}` : ''}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        )}

        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={endOthers} disabled={busy !== null || others.length === 0}>
            {busy === 'others' && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
            Sign out other devices
          </Button>
          <Button variant="destructive" onClick={() => setConfirmAll(true)} disabled={busy !== null}>
            Sign out everywhere
          </Button>
        </div>
      </CardContent>

      <AlertDialog open={confirmAll} onOpenChange={setConfirmAll}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Sign out everywhere?</AlertDialogTitle>
            <AlertDialogDescription>
              Every device signed in to your account is signed out, this one included. You'll need your password to sign back in.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={endAll}>Sign out everywhere</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
